import { Controller, useFormContext } from "react-hook-form";
import { useRecoilState, useRecoilValue } from "recoil";
import CheckBoxHookForm2 from "../../../../components/CheckBoxHookForm_2";
import { useEffect, useState } from "react";
import {
  busLicensesAddState,
  busLicensesEditState,
  bLPAttachments,
  bLPAttachmentsForDelete,
  businessLicenseOfficeId,
  disableBLOfficeField,
} from "../atoms/atoms";
import SelectHookFormSingle from "../../../../components/SelectHookFormSingle";
import TextInputLabelHookForm from "../../../../components/TextInputLabelHookForm";
import { useSFGetOffice } from "../../OfficeSettings/hooks/useSFGetOffice";
import { IBusLicensesAndPermitsDetails } from "../types";
import { useGetVendors } from "../hooks/useGetVendors";
import { useGetBusLicensePermitType } from "../api/getBLPTypes";
import { useGetBusLicensePermitRenewalStatusOptions } from "../hooks/useGetBusLicensePermitRenewalStatusOptions";
import { useGetBusLicensePermitLicenseStatusOptions } from "../hooks/useGetBusLicensePermitLicenseStatusOptions";
import TextInputHookFormDisabled from "../../../../components/TextInputHookFormDisabled";
import { toast } from "../../../../components/toastNotification";
import MinusButton from "../../../../components/MinusButton";

interface BusLicensesAndPermitsDetailsProps {
  rowForEdit: IBusLicensesAndPermitsDetails | undefined;
}

const BusLicensesAndPermitsDetails: React.FC<
  BusLicensesAndPermitsDetailsProps
> = ({ rowForEdit }) => {
  const { register, watch, setValue } = useFormContext();
  const formMethods = useFormContext();
  const isAddState = useRecoilValue(busLicensesAddState);
  const isEditState = useRecoilValue(busLicensesEditState);
  const blOfficeId = useRecoilValue(businessLicenseOfficeId);
  const [disableOfficeField] = useRecoilState(disableBLOfficeField);

  const [attachments, setAttachments] =
    useRecoilState<(File | null)[]>(bLPAttachments);

  const [attachmentDisplay, setAttachmentDisplay] = useState<any>(
    watch("fileNames") ? watch("fileNames") : [],
  );

  const [attachmentForDelete, setAttachmentForDelete] = useRecoilState<any>(
    bLPAttachmentsForDelete,
  );

  const { data: renewalStatusOptions } =
    useGetBusLicensePermitRenewalStatusOptions();

  const RENEWAL_STATUS_OPTIONS =
    renewalStatusOptions?.map((x) => ({
      label: x.renewalStatusName,
      value: Number(x.renewalStatusId),
    })) ?? [];

  const { data: licenseStatusOptions } =
    useGetBusLicensePermitLicenseStatusOptions();

  const LICENSE_STATUS_OPTIONS =
    licenseStatusOptions?.map((x) => ({
      label: x.licenseStatusName,
      value: Number(x.licenseStatusId),
    })) ?? [];

  const licenseStatus = watch("licenseStatus");
  const licenseRequired = watch("isLicenseRequired");
  const renewedDate = watch("renewedDate");

  const hasLicenseStatus =
    licenseStatus !== null &&
    licenseStatus !== undefined &&
    licenseStatus !== "";

  const selectedLicenseStatus = LICENSE_STATUS_OPTIONS.find(
    (option) => Number(option.value) === Number(licenseStatus),
  );

  const isActiveLicenseStatus =
    selectedLicenseStatus?.label?.trim().toLowerCase() === "active";

  const canSelectLicenseRequired = hasLicenseStatus && isActiveLicenseStatus;

  const licenseNumberRequired = isActiveLicenseStatus && !!licenseRequired;

  const isRenewedDateEmpty = !renewedDate;

  useEffect(() => {
    if (!isActiveLicenseStatus) {
      setValue("isLicenseRequired", false);
    }
  }, [isActiveLicenseStatus, setValue]);

  useEffect(() => {
    if (!isEditState && isRenewedDateEmpty) {
      setValue("renewalStatus", null);
    }
  }, [isEditState, isRenewedDateEmpty, setValue]);

  const removeFile = (fileName) => {
    setAttachmentDisplay(
      attachmentDisplay.filter(
        (x) => x.businessLicensePermitFileName !== fileName,
      ),
    );

    setAttachments(attachments.filter((x) => x?.name !== fileName));

    const fileToDelete = attachmentDisplay.find(
      (x) => x.businessLicensePermitFileName === fileName,
    );

    setAttachmentForDelete([...attachmentForDelete, fileToDelete]);
  };

  useEffect(() => {
    if (!isEditState || !isAddState) {
      setAttachments([]);
    }

    if (blOfficeId) {
      setValue("officeId", blOfficeId);
    }
  }, [isEditState, isAddState, blOfficeId, setAttachments, setValue]);

  const { data: agencies } = useSFGetOffice();

  const sfOfficeNameOpt = agencies?.map((x) => ({
    name: x.businessOperation,
    id: x.officeCode,
    value: x.officeId,
    label: `${x.officeName} – ${x.officeCode}`,
  }));

  const { data: issuer } = useGetVendors();
  const issuerOpt = issuer?.data?.map((x) => ({
    label: x.name,
    value: x.id,
    phone: x.phone,
  }));

  const { data: businessLicensePermitTypes = [] } =
    useGetBusLicensePermitType();

  const businessLPTypeOpt = businessLicensePermitTypes.map((item) => ({
    label: item.busLicensePermitTypeName,
    value: Number(item.busLicensePermitTypeId),
  }));

  return (
    <div className="bg-bgDB space-y-2">
      {/* Main Section */}
      <div className="bg-white box-shadow rounded-lg h-full">
        <div className="text-quinary font-bold font-sans py-2 px-4">
          Main Section
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 mx-4 pb-2 gap-2 gap-x-2">
          <div className="space-y-1 mr-6">
            <TextInputHookFormDisabled
              label="Record Number"
              type="text"
              register={register("recordName", {})}
            />
          </div>

          <div className="hidden md:block" />

          <div className="space-y-1 mr-6">
            <Controller
              name="officeId"
              control={formMethods.control}
              rules={{ required: true }}
              render={({ field }) => (
                <SelectHookFormSingle
                  field={field}
                  onChange={(e) => field.onChange(e?.value ?? null)}
                  value={field.value}
                  options={sfOfficeNameOpt}
                  label="Office Name"
                  className={
                    formMethods.formState.errors.officeId
                      ? "border-2 border-red-500 col-span-2"
                      : undefined
                  }
                  required={true}
                  isDisabled={disableOfficeField}
                />
              )}
            />
          </div>
        </div>
      </div>

      {/* Licenses and Permits Detail Section */}
      <div className="bg-white box-shadow rounded-lg h-full">
        <div className="text-quinary font-bold font-sans py-2 px-4">
          Licenses and Permits Detail Section
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 mx-4 pb-2 gap-2 gap-x-2">
          <div className="space-y-1 mr-6">
            <Controller
              name="issuer"
              control={formMethods.control}
              rules={{ required: false }}
              render={({ field }) => (
                <SelectHookFormSingle
                  field={field}
                  onChange={(e) => {
                    field.onChange(e?.value ?? null);
                    setValue("issuerPhone", e?.phone);
                  }}
                  value={field.value}
                  options={issuerOpt}
                  label="Issuer"
                  className={
                    formMethods.formState.errors.issuer
                      ? "border-2 border-red-500 col-span-2"
                      : undefined
                  }
                  required={false}
                />
              )}
            />

            <TextInputHookFormDisabled
              name="issuerPhone"
              label="Issuer Phone"
              type="text"
              register={register("issuerPhone", {})}
            />

            <Controller
              name="type"
              control={formMethods.control}
              rules={{ required: false }}
              render={({ field }) => (
                <SelectHookFormSingle
                  field={field}
                  onChange={(e) => field.onChange(e?.value ?? null)}
                  value={field.value}
                  options={businessLPTypeOpt}
                  label="Type"
                  className={
                    formMethods.formState.errors.type
                      ? "border-2 border-red-500 col-span-2"
                      : undefined
                  }
                  required={false}
                />
              )}
            />

            <TextInputLabelHookForm
              name="issuedDate"
              label="Issued Date"
              type="date"
              register={formMethods.register("issuedDate", {})}
            />

            <TextInputLabelHookForm
              name="renewedDate"
              label="Renewed Date"
              type="date"
              register={formMethods.register("renewedDate", {})}
            />

            <Controller
              name="renewalStatus"
              control={formMethods.control}
              rules={{ required: false }}
              render={({ field }) => (
                <SelectHookFormSingle
                  field={field}
                  onChange={(e) => field.onChange(e?.value ?? null)}
                  value={field.value}
                  options={RENEWAL_STATUS_OPTIONS}
                  label="Renewal Status"
                  required={false}
                  className={undefined}
                  isDisabled={false}
                />
              )}
            />

            <TextInputLabelHookForm
              name="expirationDate"
              label="Expiration Date"
              type="date"
              register={formMethods.register("expirationDate", {})}
            />
          </div>

          <div className="space-y-1 mr-6">
            <Controller
              name="licenseStatus"
              control={formMethods.control}
              rules={{ required: false }}
              render={({ field }) => (
                <SelectHookFormSingle
                  field={field}
                  onChange={(e) => field.onChange(e?.value ?? null)}
                  value={field.value}
                  options={LICENSE_STATUS_OPTIONS}
                  label="License Status"
                  required={false}
                  className={undefined}
                  isDisabled={false}
                />
              )}
            />

            <Controller
              name="isLicenseRequired"
              control={formMethods.control}
              render={({ field }) => (
                <div className="space-y-1 w-full text-xs">
                  <div className="grid grid-cols-1 md:grid-cols-3 content-center items-center w-full gap-2 py-1">
                    <label
                      htmlFor="isLicenseRequired"
                      className="flex text-left expandedLabel fontExpand"
                    >
                      License Required
                    </label>

                    <input
                      type="checkbox"
                      id="isLicenseRequired"
                      name="isLicenseRequired"
                      className="w-4 h-4 border-gray-50 cursor-pointer hover:cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                      checked={!!field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                      disabled={!canSelectLicenseRequired}
                    />
                  </div>
                </div>
              )}
            />

            <TextInputLabelHookForm
              name="licenseOrPermitNumber"
              label="License/Permit Number"
              type="text"
              required={licenseNumberRequired}
              className={
                formMethods.formState.errors.licenseOrPermitNumber
                  ? "border-2 border-red-500 col-span-2"
                  : undefined
              }
              register={formMethods.register("licenseOrPermitNumber", {
                required: licenseNumberRequired
                  ? {
                      value: true,
                      message: "Required Field",
                    }
                  : false,
              })}
              maxLength={255}
            />

            {isEditState && (
              <CheckBoxHookForm2
                label="Old License"
                name="isOldLicense"
                validation={{}}
                hasPermission={true}
                className=""
                register={formMethods.register}
                disabled={false}
              />
            )}
          </div>
        </div>
      </div>

      {/* Notes Section */}
      <div className="bg-white box-shadow rounded-lg h-full">
        <div className="text-quinary font-bold font-sans py-2 px-4">
          Notes Section
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 mx-4 mr-5 pb-2">
          <div className="space-y-1 mr-6">
            <TextInputLabelHookForm
              name="notes"
              label="Notes"
              type="text"
              register={formMethods.register("notes", {})}
              maxLength={255}
            />
          </div>
        </div>
      </div>

      {/* Attachments Section */}
      <div className="bg-white box-shadow rounded-lg h-full">
        <div className="text-quinary font-bold font-sans py-2 px-4">
          Attachments Section
        </div>

        <div className="space-y-1 mx-2">
          <div className="text-xs">
            <div>
              {attachmentDisplay?.length > 0 &&
                attachmentDisplay?.map((file, i) => (
                  <div
                    key={i}
                    className="bg-LightWhite flex justify-between px-2 py-1 mx-2"
                  >
                    <div className="font-semibold text-left text-primary pr-2">
                      {file.businessLicensePermitFileName}
                    </div>

                    <div className="flex items-center">
                      <MinusButton
                        onClick={() =>
                          removeFile(file.businessLicensePermitFileName)
                        }
                      />
                    </div>
                  </div>
                ))}
            </div>

            <div className="flex justify-center py-2">
              <label className="text-center bg-primary text-white py-1 px-3 border font-medium rounded-full cursor-pointer hover:bg-white hover:text-primary hover:border-primary">
                <input
                  type="file"
                  className="hidden"
                  multiple={true}
                  onChange={(e) => {
                    let allFiles = attachmentDisplay.map((item) => item);

                    let allAttachments = attachments.map((x) => x);

                    if (e.target.files && e.target.files.length > 0) {
                      for (let i = 0; i < e.target.files.length; ++i) {
                        const file = e.target.files[i];

                        const rowIndex = attachmentDisplay.findIndex(
                          (obj) =>
                            obj.businessLicensePermitFileName === file.name,
                        );

                        if (rowIndex < 0) {
                          allFiles = [
                            ...allFiles,
                            {
                              businessLicensePermitFileName: file.name,
                            },
                          ];

                          setAttachmentDisplay(allFiles);

                          const newFile = e.target.files?.item(i) ?? null;

                          allAttachments = [...allAttachments, newFile];

                          setAttachments(allAttachments);
                        } else {
                          toast.fire(
                            "Attachment failed",
                            "File already exist",
                            "error",
                          );
                        }
                      }
                    }
                  }}
                />
                Choose File/s
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* System Information */}
      {formMethods.watch("recordId") && isEditState && (
        <div className="bg-white box-shadow rounded-lg h-full">
          <div className="text-quinary font-bold font-sans py-2 px-4">
            System Information
          </div>

          <div className="grid grid-cols-1 md:grid-cols-1 mx-4 mr-5 pb-2 gap-12">
            <div className="space-y-1 mr-6">
              <div className="grid grid-cols-1 md:grid-cols-2 mr-5 pb-2 gap-12">
                <div className="space-y-1 mr-6 mt-1">
                  <TextInputHookFormDisabled
                    label="Last Modified By"
                    name="modifiedBy"
                    register={formMethods.register("modifiedBy", {})}
                  />
                </div>

                <div className="space-y-1 mr-6">
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Last Modified Date:
                    </label>

                    <div className="flex space-x-1 justify-start expandedLabel fontExpand">
                      <span className="pr-2">
                        {rowForEdit?.modifiedDate &&
                          new Date(
                            rowForEdit.modifiedDate,
                          ).toLocaleDateString() +
                            " " +
                            new Date(
                              rowForEdit.modifiedDate,
                            ).toLocaleTimeString(navigator.language, {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusLicensesAndPermitsDetails;